import React, { useState } from "react";
import {
  MessageSquare,
  Zap,
  TrendingUp,
  CheckCircle,
  Edit3,
  Play,
  Pause,
  Eye,
  Copy,
  Plus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AutomationTemplate {
  id: number;
  name: string;
  description: string;
  icon: string;
  triggers: string[];
  steps: number;
  category: string;
}

interface ActiveAutomation {
  id: number;
  name: string;
  status: string;
  sent: number;
  delivered: number;
  clicks: number;
  conversions: number;
  created: string;
  lastRun: string;
}

interface DraftAutomation {
  id: number;
  name: string;
  status: string;
  created: string;
  lastEdited: string;
}

const SMSAutomationPlatform = () => {
  const navigate = useNavigate();
  function onNewJourney() {
    navigate("/journey/new");
  }
    useState<ActiveAutomation | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AutomationTemplate | null>(null);

  const automationTemplates: AutomationTemplate[] = [
    {
      id: 1,
      name: "Welcome Series",
      description: "Send welcome messages to new subscribers",
      icon: "👋",
      triggers: ["New Subscriber", "Opt-in"],
      steps: 3,
      category: "Onboarding",
    },
    {
      id: 2,
      name: "Birthday Campaign",
      description: "Automated birthday wishes with special offers",
      icon: "🎂",
      triggers: ["Birthday Date"],
      steps: 2,
      category: "Engagement",
    },
    {
      id: 3,
      name: "Abandoned Cart Recovery",
      description: "Recover lost sales with reminder messages",
      icon: "🛒",
      triggers: ["Cart Abandonment"],
      steps: 4,
      category: "E-commerce",
    },
    {
      id: 4,
      name: "Event Reminder",
      description: "Send reminders before important events",
      icon: "📅",
      triggers: ["Date/Time"],
      steps: 2,
      category: "Events",
    },
    {
      id: 5,
      name: "Re-engagement Campaign",
      description: "Win back inactive subscribers",
      icon: "🔄",
      triggers: ["Inactivity Period"],
      steps: 3,
      category: "Retention",
    },
    {
      id: 6,
      name: "Survey Follow-up",
      description: "Follow up after customer surveys",
      icon: "📋",
      triggers: ["Survey Completion"],
      steps: 2,
      category: "Feedback",
    },
  ];

  const activeAutomations: ActiveAutomation[] = [
    {
      id: 1,
      name: "Welcome Series - New Customers",
      status: "active",
      sent: 1245,
      delivered: 1198,
      clicks: 89,
      conversions: 23,
      created: "2024-06-15",
      lastRun: "2 hours ago",
    },
    {
      id: 2,
      name: "Birthday Special Offers",
      status: "active",
      sent: 567,
      delivered: 545,
      clicks: 67,
      conversions: 12,
      created: "2024-06-10",
      lastRun: "1 day ago",
    },
    {
      id: 3,
      name: "Event Reminder - Webinar",
      status: "active",
      sent: 892,
      delivered: 876,
      clicks: 124,
      conversions: 78,
      created: "2024-06-20",
      lastRun: "3 hours ago",
    },
  ];

  const draftAutomations: DraftAutomation[] = [
    {
      id: 4,
      name: "Holiday Sale Campaign",
      status: "draft",
      created: "2024-06-25",
      lastEdited: "1 hour ago",
    },
    {
      id: 5,
      name: "Product Launch Sequence",
      status: "draft",
      created: "2024-06-22",
      lastEdited: "2 days ago",
    },
  ];

  const analyticsData = {
    totalSent: 2704,
    totalDelivered: 2619,
    avgDeliveryRate: 96.9,
    totalClicks: 280,
    avgClickRate: 10.7,
    totalConversions: 113,
    avgConversionRate: 4.3,
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Side Navigation */}

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Journeys</h2>

            <button
              onClick={onNewJourney}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Journey
            </button>
          </div>

          {/* Automation Templates */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Ready-to-Use Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {automationTemplates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center mb-3">
                    <span className="text-2xl mr-3">{template.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        {template.name}
                      </h4>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        {template.category}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>{template.steps} steps</span>
                    <span>Triggers: {template.triggers.join(", ")}</span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setShowTemplateModal(true);
                    }}
                    className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm"
                  >
                    Use Template
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Analytics Overview */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Journey Analytics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Sent</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {analyticsData.totalSent.toLocaleString()}
                    </p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Delivery Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {analyticsData.avgDeliveryRate}%
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Click Rate</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {analyticsData.avgClickRate}%
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Conversion Rate</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {analyticsData.avgConversionRate}%
                    </p>
                  </div>
                  <Zap className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Active and Draft Automations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Automations */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Active Journeys
                  </h3>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                    {activeAutomations.length} Running
                  </span>
                </div>
              </div>
              <div className="divide-y">
                {activeAutomations.map((automation) => (
                  <div key={automation.id} className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800">
                        {automation.name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Pause className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Sent</p>
                        <p className="font-medium">{automation.sent}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Delivered</p>
                        <p className="font-medium text-green-600">
                          {automation.delivered}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Clicks</p>
                        <p className="font-medium text-blue-600">
                          {automation.clicks}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Conversions</p>
                        <p className="font-medium text-purple-600">
                          {automation.conversions}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                      <span>Last run: {automation.lastRun}</span>
                      <span className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                        Active
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Draft Automations */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Draft Automations
                  </h3>
                  <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
                    {draftAutomations.length} Drafts
                  </span>
                </div>
              </div>
              <div className="divide-y">
                {draftAutomations.map((automation) => (
                  <div key={automation.id} className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800">
                        {automation.name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-green-600 hover:text-green-700">
                          <Play className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 mb-3">
                      <p>Created: {automation.created}</p>
                      <p>Last edited: {automation.lastEdited}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center text-xs text-gray-500">
                        <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                        Draft
                      </span>
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                        Continue Setup
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Configuration Modal */}
      {showTemplateModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Configure {selectedTemplate.name}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                defaultValue={selectedTemplate.name}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>All Contacts</option>
                <option>New Subscribers</option>
                <option>VIP Customers</option>
                <option>Custom Segment</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Condition
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                {selectedTemplate.triggers.map((trigger, index) => (
                  <option key={index}>{trigger}</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Here you would typically save the automation
                  alert(
                    `${selectedTemplate.name} automation created successfully!`
                  );
                  setShowTemplateModal(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create Journey
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSAutomationPlatform;
